import type { PlanStatus } from "../plan/plan-api.js";

export type ChecklistStepKey =
  | "business"
  | "equipment"
  | "team"
  | "suppliers"
  | "cleaning"
  | "plan"
  | "registration"
  | "assign"
  | "water"
  | "risks"
  | "methods"
  | "documents";

export type SetupSectionKey =
  | "checklist"
  | "locations"
  | "equipment"
  | "answers"
  | "suppliers"
  | "cleaning"
  | "plan"
  | "registration"
  | "assign"
  | "water"
  | "risks"
  | "methods"
  | "documents";

export interface SetupTarget {
  search?: { section: SetupSectionKey };
  to: "/setup" | "/team";
}

export interface ChecklistStep {
  detail: string | null;
  done: boolean;
  key: ChecklistStepKey;
  label: string;
  // Optional steps sit under "Before your verification" and never hold up a
  // ready kitchen.
  optional: boolean;
  target: SetupTarget;
}

export interface ChecklistInput {
  assetCount: number;
  checks: readonly CheckAssignment[];
  cleaningCount: number;
  documentCount: number;
  hasLocation: boolean;
  hasTeam: boolean;
  methodCount: number;
  planStatus: PlanStatus;
  // A council or MPI registration covers this location.
  registered: boolean;
  riskCount: number;
  supplierCount: number;
  waterCount: number;
}

export interface CheckAssignment {
  assignee_staff_profile_ids: readonly string[];
  id: string;
}

export interface PersonInvitationState {
  id: string;
  invitation?: unknown;
  user_id: string | null;
}

// Where Today's "Assign people" button goes.
export const ASSIGN_PEOPLE_TARGET: SetupTarget = {
  search: { section: "assign" },
  to: "/setup",
};

const sectionKeys: readonly SetupSectionKey[] = [
  "checklist",
  "locations",
  "equipment",
  "answers",
  "suppliers",
  "cleaning",
  "plan",
  "registration",
  "assign",
  "water",
  "risks",
  "methods",
  "documents",
];

export function parseSection(value: unknown): SetupSectionKey {
  return sectionKeys.find((key) => key === value) ?? "checklist";
}

export function unassignedChecks<T extends CheckAssignment>(
  checks: readonly T[],
): T[] {
  return checks.filter(
    (check) => check.assignee_staff_profile_ids.length === 0,
  );
}

function countDetail(count: number): string | null {
  return count > 0 ? String(count) : null;
}

// Three steps make a kitchen ready: the business, its fridges and freezers,
// and its people. Everything else waits under "Before your verification".
export function buildChecklist(input: ChecklistInput): ChecklistStep[] {
  const unassigned = unassignedChecks(input.checks).length;
  const total = input.checks.length;
  const section = (key: SetupSectionKey): SetupTarget => ({
    search: { section: key },
    to: "/setup",
  });
  const later = (
    key: ChecklistStepKey,
    label: string,
    count: number,
    target: SetupSectionKey,
  ): ChecklistStep => ({
    detail: countDetail(count),
    done: count > 0,
    key,
    label,
    optional: true,
    target: section(target),
  });
  return [
    {
      detail: null,
      done: input.hasLocation,
      key: "business",
      label: "Business",
      optional: false,
      target: section("locations"),
    },
    {
      detail: countDetail(input.assetCount),
      done: input.assetCount > 0,
      key: "equipment",
      label: "Fridges and freezers",
      optional: false,
      target: section("equipment"),
    },
    {
      detail: null,
      done: input.hasTeam,
      key: "team",
      label: "People",
      optional: false,
      target: { to: "/team" },
    },
    {
      detail: null,
      done: input.planStatus === "reviewed",
      key: "plan",
      label: "Review your plan",
      // A new location gets the default plan, and its checks, straight away;
      // reviewing it never holds anything up. Only a location made before
      // that ("none") needs a plan to have any checks.
      optional: input.planStatus !== "none",
      target: section("plan"),
    },
    {
      detail: null,
      done: input.registered,
      key: "registration",
      label: "Registration",
      optional: true,
      target: section("registration"),
    },
    later("suppliers", "Suppliers", input.supplierCount, "suppliers"),
    later("cleaning", "Cleaning plan", input.cleaningCount, "cleaning"),
    {
      detail: total > 0 ? `${total - unassigned} of ${total}` : null,
      done: total > 0 && unassigned === 0,
      key: "assign",
      label: "Who does each check",
      optional: true,
      target: section("assign"),
    },
    later("water", "Water", input.waterCount, "water"),
    later("risks", "Nearby risks", input.riskCount, "risks"),
    later("methods", "Proven methods", input.methodCount, "methods"),
    later("documents", "Documents", input.documentCount, "documents"),
  ];
}

export function checklistProgress(steps: readonly ChecklistStep[]): {
  complete: boolean;
  done: number;
  next: ChecklistStep | null;
  percent: number;
  total: number;
} {
  const required = steps.filter((step) => !step.optional);
  const done = required.filter((step) => step.done).length;
  return {
    complete: done === required.length,
    done,
    next: required.find((step) => !step.done) ?? null,
    percent:
      required.length === 0 ? 0 : Math.round((done / required.length) * 100),
    total: required.length,
  };
}

// True once someone other than the signed-in person has joined or been invited.
export function hasInvitedTeam(
  people: readonly PersonInvitationState[],
  ownIds: readonly string[],
): boolean {
  return people.some(
    (person) =>
      !ownIds.includes(person.id) &&
      (person.user_id !== null || Boolean(person.invitation)),
  );
}

export type AssignmentChange =
  | { assign_everyone: true; id: string }
  | { assignee_staff_profile_ids: string[]; id: string };

// Each chosen check ends up with exactly the chosen people. Choosing
// everyone sets the check to everyone at the location, which follows people
// as they join or leave (founder decision, 24 Oct 2026).
export function assignmentChanges(
  checkIds: readonly string[],
  personIds: readonly string[],
  everyone = false,
): AssignmentChange[] {
  const people = [...new Set(personIds)];
  return [...new Set(checkIds)].map((id) =>
    everyone
      ? { assign_everyone: true as const, id }
      : { assignee_staff_profile_ids: people, id },
  );
}

export function toggleId(ids: readonly string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((entry) => entry !== id) : [...ids, id];
}

// One person's switch on one check. Turning someone off a check that is for
// everyone names everyone else, so the check stops following new people.
export function personCheckChange(
  check: CheckAssignment,
  personId: string,
  on: boolean,
): AssignmentChange {
  const others = check.assignee_staff_profile_ids.filter(
    (id) => id !== personId,
  );
  return {
    assignee_staff_profile_ids: on ? [...others, personId] : others,
    id: check.id,
  };
}
